// userSlice.js = Redux Toolkit slice for user management
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { userAPI } from "./api";
import { logout } from "../auth/index";
import { createMiddleware } from "../middlewareUtil";

// Initial state
const initialState = {
  loading: false,
  loaded: false,
  saving: false,
  saved: false,
  deleting: false,
  deleted: false,
  user: {
    firstName: "",
    lastName: "",
    organization: "",
    phoneNumber: "",
    email: "",
    enabled: true,
    password: "",
    confirmPassword: "",
  },
};

export const forceLogoutUser = createAsyncThunk(
  "user/forceLogoutUser",
  async (userId, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await userAPI.forceLogoutUser(accessToken, userId);

    if (result.success) {
      return userId;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to force logout user");
  }
);

export const loadUser = createAsyncThunk(
  "user/loadUser",
  async (userId, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await userAPI.loadUser(accessToken, userId);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to load user");
  }
);

export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async ({ userId, clipsOnly }, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await userAPI.deleteUser(accessToken, userId, clipsOnly);

    if (result.success) {
      return userId;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to delete user");
  }
);

export const saveUser = createAsyncThunk(
  "user/saveUser",
  async (userData, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await userAPI.saveUser(accessToken, userData);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to save user");
  }
);

// Create the user slice
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    reset: (_state, _action) => cloneDeep(initialState),
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Load user
      .addCase(loadUser.pending, (state, _action) => {
        state.loading = true;
        state.loaded = false;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.user = action.payload;
        state.user.password = ""; // Clear password after loading
        state.user.confirmPassword = ""; // Clear confirm password after loading
      })
      .addCase(loadUser.rejected, (_state, _action) => {
        return cloneDeep(initialState);
      })

      // Delete user
      .addCase(deleteUser.pending, (state, _action) => {
        state.deleting = true;
        state.deleted = false;
      })
      .addCase(deleteUser.fulfilled, (state, _action) => {
        state.deleting = false;
        state.deleted = true;
        state.user = cloneDeep(initialState.user);
      })
      .addCase(deleteUser.rejected, (state, _action) => {
        state.deleting = false;
      })

      // Save user
      .addCase(saveUser.pending, (state, _action) => {
        state.saving = true;
        state.saved = false;
      })
      .addCase(saveUser.fulfilled, (state, action) => {
        state.saving = false;
        state.saved = true;
        state.user = action.payload;
        state.user.password = ""; // Clear password after saving
        state.user.confirmPassword = ""; // Clear confirm password after saving
      })
      .addCase(saveUser.rejected, (state, _action) => {
        state.saving = false;
      });
  },
});

export const userMiddleware = createMiddleware([
  "user/forceLogoutUser/pending",
  "user/loadUser/pending",
  "user/deleteUser/pending",
  "user/saveUser/pending",
]);

// Export actions
export const { reset, updateUser } = userSlice.actions;

// Export reducer
export default userSlice.reducer;
